import React, { useEffect, useState } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import { Badge } from "../ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  Type,
  CircleDot,
  ListChecks,
  ShieldCheck,
  Trash2,
  ChevronUp,
  ChevronDown,
  Plus,
  X,
  Paperclip,
} from "lucide-react";

/**
 * Il compositore del modulo contatti.
 *
 * Un modulo solo per bot, quattro tipi di campo. La validazione vera la fa il
 * server: qui si guida la scelta, non si fa da guardia.
 */

const TIPI = [
  {
    type: "text",
    icon: Type,
    label: "Testo",
    hint: "Testo libero, email o telefono",
  },
  {
    type: "single_choice",
    icon: CircleDot,
    label: "Scelta singola",
    hint: "Una sola opzione fra quelle previste",
  },
  {
    type: "multi_choice",
    icon: ListChecks,
    label: "Scelta multipla",
    hint: "Più opzioni, con minimo e massimo",
  },
  {
    type: "consent",
    icon: ShieldCheck,
    label: "Consenso",
    hint: "Casella da spuntare, con link facoltativo",
  },
  {
    type: "file",
    icon: Paperclip,
    label: "File",
    hint: "Immagine o PDF caricato dal visitatore, max 10 MB",
  },
];

const FILE_ACCETTATI = [
  { value: "both", label: "Immagini e PDF" },
  { value: "images", label: "Solo immagini (JPG, PNG, WEBP)" },
  { value: "documents", label: "Solo PDF" },
];

const MODI_TESTO = [
  { value: "free", label: "Testo libero" },
  { value: "email", label: "Email" },
  { value: "phone", label: "Telefono" },
];

function campoNuovo(type) {
  const base = { _key: Math.random().toString(36).slice(2), type, label: "", required: false };
  if (type === "text") return { ...base, mode: "free", placeholder: "" };
  if (type === "single_choice") return { ...base, options: ["", ""] };
  // Senza massimo: si possono scegliere tutte le opzioni, anche quelle aggiunte dopo.
  if (type === "multi_choice") return { ...base, options: ["", ""], min: 0 };
  if (type === "file") return { ...base, accept: "both" };
  return { ...base, text: "", link_url: "", link_label: "" };
}

/**
 * Casella numerica che si puo' svuotare mentre si scrive: il valore si applica
 * appena e' un numero, e uscendo dalla casella torna quello valido. Senza, a
 * casella vuota il valore diventava subito 1 e scrivendo 3 si otteneva 13.
 */
function InputNumero({ value, onValore, ...props }) {
  const [testo, setTesto] = useState(String(value));
  useEffect(() => setTesto(String(value)), [value]);
  return (
    <Input
      type="number"
      {...props}
      value={testo}
      onChange={(e) => {
        const scritto = e.target.value;
        setTesto(scritto);
        if (scritto !== "" && Number.isFinite(Number(scritto))) onValore(Number(scritto));
      }}
      onBlur={() => setTesto(String(value))}
    />
  );
}

function EditorOpzioni({ field, onChange }) {
  const options = field.options || [];

  const setOption = (index, value) => {
    const next = [...options];
    next[index] = value;
    onChange({ ...field, options: next });
  };

  const remove = (index) => {
    const next = options.filter((_, i) => i !== index);
    const patch = { ...field, options: next };
    // Il massimo non può superare il numero di opzioni rimaste.
    if (field.type === "multi_choice") {
      patch.max = Math.min(field.max || next.length, next.length) || next.length;
      patch.min = Math.min(field.min || 0, patch.max);
    }
    onChange(patch);
  };

  return (
    <div className="space-y-2">
      <Label className="text-xs font-medium text-muted-foreground">Opzioni</Label>
      {options.map((option, index) => (
        <div className="flex items-center gap-2" key={index}>
          <Input
            value={option}
            placeholder={`Opzione ${index + 1}`}
            onChange={(e) => setOption(index, e.target.value)}
          />
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="shrink-0"
            onClick={() => remove(index)}
            disabled={options.length <= 2}
            aria-label={`Togli opzione ${index + 1}`}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      ))}
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => {
          const next = [...options, ""];
          const patch = { ...field, options: next };
          // Se il massimo era "tutte" resta "tutte" anche con l'opzione nuova
          // (il server salva il numero di opzioni come massimo).
          if (field.type === "multi_choice" && field.max != null && field.max >= options.length) {
            patch.max = next.length;
          }
          onChange(patch);
        }}
      >
        <Plus className="h-4 w-4 mr-1" />
        Aggiungi opzione
      </Button>
      {options.length < 2 ? (
        <p className="text-xs text-destructive">Servono almeno due opzioni.</p>
      ) : null}
    </div>
  );
}

function EditorCampo({ field, index, total, onChange, onRemove, onMove }) {
  const meta = TIPI.find((t) => t.type === field.type);
  const Icon = meta?.icon || Type;
  const opzioni = field.options?.length || 0;

  return (
    <Card className="rounded-[14px]">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
            <CardTitle className="text-base truncate">
              {field.label || field.text || meta?.label || "Campo"}
            </CardTitle>
            <Badge variant="secondary" className="shrink-0">
              {meta?.label}
            </Badge>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => onMove(index, -1)}
              disabled={index === 0}
              aria-label="Sposta su"
            >
              <ChevronUp className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => onMove(index, 1)}
              disabled={index === total - 1}
              aria-label="Sposta giù"
            >
              <ChevronDown className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => onRemove(index)}
              aria-label="Togli campo"
            >
              <Trash2 className="h-4 w-4 text-destructive" />
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {field.type === "consent" ? (
          <>
            <div className="space-y-1.5">
              <Label>Testo del consenso</Label>
              <Textarea
                rows={2}
                value={field.text || ""}
                placeholder="Ho letto e accetto l'informativa sulla privacy"
                onChange={(e) => onChange(index, { ...field, text: e.target.value })}
              />
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label>Link (facoltativo)</Label>
                <Input
                  value={field.link_url || ""}
                  placeholder="https://tuosito.it/privacy"
                  onChange={(e) => onChange(index, { ...field, link_url: e.target.value })}
                  // "tuosito.it/privacy" diventa "https://tuosito.it/privacy":
                  // senza, il server lo scarta e in chat il link non compare.
                  onBlur={(e) => {
                    const url = e.target.value.trim();
                    if (url && !/^[a-z][a-z0-9+.-]*:/i.test(url)) {
                      onChange(index, { ...field, link_url: `https://${url}` });
                    }
                  }}
                />
                {field.link_url && !/^https?:\/\//i.test(field.link_url.trim()) ? (
                  <p className="text-xs text-destructive">
                    Il link deve iniziare con https://: così com'è in chat non compare.
                  </p>
                ) : null}
              </div>
              <div className="space-y-1.5">
                <Label>Testo del link</Label>
                <Input
                  value={field.link_label || ""}
                  placeholder="Scopri di più"
                  disabled={!field.link_url}
                  onChange={(e) => onChange(index, { ...field, link_label: e.target.value })}
                />
              </div>
            </div>
          </>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Etichetta</Label>
              <Input
                value={field.label || ""}
                placeholder="Nome e cognome"
                onChange={(e) => onChange(index, { ...field, label: e.target.value })}
              />
            </div>

            {field.type === "text" ? (
              <div className="space-y-1.5">
                <Label>Tipo di dato</Label>
                <Select
                  value={field.mode || "free"}
                  onValueChange={(mode) => onChange(index, { ...field, mode })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MODI_TESTO.map((m) => (
                      <SelectItem key={m.value} value={m.value}>
                        {m.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : null}

            {field.type === "file" ? (
              <div className="space-y-1.5">
                <Label>File accettati</Label>
                <Select
                  value={field.accept || "both"}
                  onValueChange={(accept) => onChange(index, { ...field, accept })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FILE_ACCETTATI.map((m) => (
                      <SelectItem key={m.value} value={m.value}>
                        {m.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : null}
          </div>
        )}

        {field.type === "file" ? (
          <p className="text-xs text-muted-foreground">
            Un file per campo, massimo 10 MB. Il tipo si controlla sul contenuto del file, non
            sull'estensione. I file restano privati: si aprono dalla scheda del contatto.
          </p>
        ) : null}

        {field.type === "text" ? (
          <div className="space-y-1.5">
            <Label>Testo d'aiuto (facoltativo)</Label>
            <Input
              value={field.placeholder || ""}
              placeholder="Compare in grigio dentro il campo"
              onChange={(e) => onChange(index, { ...field, placeholder: e.target.value })}
            />
          </div>
        ) : null}

        {field.type === "text" && (field.mode || "free") === "free" ? (
          <div className="flex items-center justify-between rounded-[10px] border p-3">
            <div className="space-y-0.5 pr-4">
              <Label className="cursor-pointer">Risposta lunga</Label>
              <p className="text-xs text-muted-foreground">
                Casella alta più righe. Per un nome lascia spento.
              </p>
            </div>
            <Switch
              checked={Boolean(field.multiline)}
              onCheckedChange={(multiline) => onChange(index, { ...field, multiline })}
            />
          </div>
        ) : null}

        {field.type === "single_choice" || field.type === "multi_choice" ? (
          <EditorOpzioni field={field} onChange={(next) => onChange(index, next)} />
        ) : null}

        {field.type === "multi_choice" ? (
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Minimo di scelte</Label>
              <InputNumero
                min={0}
                max={opzioni}
                value={field.min ?? 0}
                onValore={(numero) => {
                  const min = Math.max(0, Math.min(Math.trunc(numero) || 0, opzioni));
                  onChange(index, {
                    ...field,
                    min,
                    max: Math.max(min || 1, field.max || opzioni),
                    // Un minimo di 1 è già un obbligo: i due interruttori non
                    // devono potersi contraddire.
                    required: min >= 1 ? true : field.required,
                  });
                }}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Massimo di scelte</Label>
              <InputNumero
                min={1}
                max={opzioni}
                value={field.max ?? opzioni}
                onValore={(numero) => {
                  const max = Math.max(1, Math.min(Math.trunc(numero) || 1, opzioni));
                  onChange(index, { ...field, max, min: Math.min(field.min || 0, max) });
                }}
              />
            </div>
          </div>
        ) : null}

        <div className="flex items-center justify-between rounded-[10px] border p-3">
          <div className="space-y-0.5 pr-4">
            <Label className="cursor-pointer">Obbligatorio</Label>
            <p className="text-xs text-muted-foreground">
              {field.type === "multi_choice" && (field.min ?? 0) >= 1
                ? "Imposto dal minimo di scelte"
                : "Senza questo campo il modulo non si può inviare"}
            </p>
          </div>
          <Switch
            checked={Boolean(field.required)}
            disabled={field.type === "multi_choice" && (field.min ?? 0) >= 1}
            onCheckedChange={(required) => onChange(index, { ...field, required })}
          />
        </div>
      </CardContent>
    </Card>
  );
}

function elencoIndirizzi(testo) {
  return testo
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}

/**
 * @param {boolean} [showEnabled] - interruttore "Modulo attivo": nascosto
 *   nella pagina dell'app in Apps & Integrazioni, che ha il suo
 */
export default function ContactFormBuilder({ form, onChange, showEnabled = true }) {
  const [aggiungiAperto, setAggiungiAperto] = useState(false);
  const fields = form.fields || [];

  // Il campo degli indirizzi tiene il testo cosi' come viene scritto. Se lo si
  // ricostruisse dalla lista a ogni tasto, la virgola appena digitata
  // sparirebbe e non si potrebbe mai scrivere un secondo indirizzo.
  const [testoIndirizzi, setTestoIndirizzi] = useState(() =>
    (form.notify_emails || []).join(", ")
  );
  const indirizziSalvati = (form.notify_emails || []).join("|");
  useEffect(() => {
    // Si riallinea solo quando la lista cambia da fuori, per esempio dopo il
    // salvataggio, quando il server scarta un indirizzo non valido.
    setTestoIndirizzi((testo) =>
      elencoIndirizzi(testo).join("|") === indirizziSalvati
        ? testo
        : indirizziSalvati.split("|").filter(Boolean).join(", ")
    );
  }, [indirizziSalvati]);

  const patch = (changes) => onChange({ ...form, ...changes });

  const setField = (index, next) => {
    const copia = [...fields];
    copia[index] = next;
    patch({ fields: copia });
  };

  const removeField = (index) => patch({ fields: fields.filter((_, i) => i !== index) });

  const moveField = (index, delta) => {
    const target = index + delta;
    if (target < 0 || target >= fields.length) return;
    const copia = [...fields];
    [copia[index], copia[target]] = [copia[target], copia[index]];
    patch({ fields: copia });
  };

  const addField = (type) => {
    patch({ fields: [...fields, campoNuovo(type)] });
    setAggiungiAperto(false);
  };

  return (
    <div className="space-y-6">
      <Card className="rounded-[14px]">
        <CardHeader>
          <CardTitle className="text-lg">Il modulo</CardTitle>
          <CardDescription>
            Quello che il visitatore vede quando l'assistente gli propone di lasciare i propri dati.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {showEnabled ? (
            <div className="flex items-center justify-between rounded-[10px] border p-4">
              <div className="space-y-0.5 pr-4">
                <Label className="text-base">Modulo attivo</Label>
                <p className="text-sm text-muted-foreground">
                  Da spento, l'assistente non lo propone mai e nessun contatto viene raccolto.
                </p>
              </div>
              <Switch
                checked={Boolean(form.enabled)}
                onCheckedChange={(enabled) => patch({ enabled })}
              />
            </div>
          ) : null}

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Titolo</Label>
              <Input
                value={form.title || ""}
                placeholder="Ti ricontattiamo noi"
                onChange={(e) => patch({ title: e.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Testo del pulsante di invio</Label>
              <Input
                value={form.submit_label || ""}
                placeholder="Invia"
                onChange={(e) => patch({ submit_label: e.target.value })}
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Descrizione</Label>
            <Textarea
              rows={2}
              value={form.description || ""}
              placeholder="Lascia i tuoi dati: ti rispondiamo entro un giorno lavorativo."
              onChange={(e) => patch({ description: e.target.value })}
            />
          </div>

          <div className="space-y-1.5">
            <Label>Messaggio di ringraziamento</Label>
            <Input
              value={form.thank_you || ""}
              placeholder="Grazie, abbiamo ricevuto la tua richiesta."
              onChange={(e) => patch({ thank_you: e.target.value })}
            />
            <p className="text-xs text-muted-foreground">
              Sostituisce il modulo in chat dopo l'invio.
            </p>
          </div>

          <div className="space-y-1.5">
            <Label>Avvisami a questi indirizzi</Label>
            <Input
              value={testoIndirizzi}
              placeholder="commerciale@tuosito.it, info@tuosito.it"
              onChange={(e) => {
                setTestoIndirizzi(e.target.value);
                patch({ notify_emails: elencoIndirizzi(e.target.value) });
              }}
            />
            <p className="text-xs text-muted-foreground">
              Separati da virgola, fino a cinque. Lasciando vuoto non parte nessuna email:
              i contatti si vedono solo qui.
            </p>
          </div>

          <div className="space-y-3 rounded-[10px] border p-4">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5 pr-4">
                <Label className="text-base">Pulsante in chat</Label>
                <p className="text-sm text-muted-foreground">
                  Sempre disponibile sopra il campo di scrittura, per chi vuole lasciare i dati
                  senza chiederlo al bot. Il bot propone il modulo anche da solo: quando, lo scrivi
                  in Apps & Integrazioni.
                </p>
              </div>
              <Switch
                checked={form.trigger_enabled !== false}
                onCheckedChange={(trigger_enabled) => patch({ trigger_enabled })}
              />
            </div>
            <div className="space-y-1.5">
              <Label>Testo del pulsante in chat</Label>
              <Input
                value={form.trigger_label ?? ""}
                placeholder="Fatti ricontattare"
                disabled={form.trigger_enabled === false}
                onChange={(e) => patch({ trigger_label: e.target.value })}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold">Campi</h3>
            <p className="text-sm text-muted-foreground">
              {fields.length === 0
                ? "Nessun campo: il modulo non si può attivare."
                : `${fields.length} camp${fields.length === 1 ? "o" : "i"}, nell'ordine in cui compaiono.`}
            </p>
          </div>
        </div>

        {fields.map((field, index) => (
          <EditorCampo
            key={field._key || field.id || index}
            field={field}
            index={index}
            total={fields.length}
            onChange={setField}
            onRemove={removeField}
            onMove={moveField}
          />
        ))}

        {aggiungiAperto ? (
          <Card className="rounded-[14px]">
            <CardContent className="grid gap-3 pt-6 sm:grid-cols-2">
              {TIPI.map(({ type, icon: Icon, label, hint }) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => addField(type)}
                  className="flex items-start gap-3 rounded-[10px] border p-3 text-left transition-colors hover:border-[#CBD5E1] hover:bg-[#F8FAFC]"
                >
                  <Icon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                  <span className="min-w-0">
                    <span className="block text-sm font-medium">{label}</span>
                    <span className="block text-xs text-muted-foreground">{hint}</span>
                  </span>
                </button>
              ))}
            </CardContent>
          </Card>
        ) : (
          <Button type="button" variant="outline" onClick={() => setAggiungiAperto(true)}>
            <Plus className="h-4 w-4 mr-1" />
            Aggiungi campo
          </Button>
        )}
      </div>
    </div>
  );
}
